import { useQuery } from '@tanstack/react-query';
import { Alert, Button, Card, Col, Flex, Row, Skeleton, Table, Typography } from 'antd';
import type { TableProps } from 'antd';
import { useMemo, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { EChart, type EChartClick, type EChartOption } from '../../components/EChart';
import { ApiError } from '../../lib/api-client';
import { formatDateTime } from '../../lib/format';
import { formatMoney } from '../../lib/money';
import {
  fetchBikesSummary,
  type BikeStockBucket,
  type BikeStockFilter,
  type BikesSummary,
  type SalesLinesFilter,
} from './api';
import { unitsTooltipFormatter } from './chart-tooltip';
import { foldBrands, soldBikesFilter, formatUnits, monthName, plotValue, type BrandSeries } from './figures';
import { BikeStockDrawer } from './BikeStockDrawer';
import { SalesLinesDrawer } from './SalesLinesDrawer';
import { Tile } from './Tile';

const { Text } = Typography;

/** The top five brands take distinct colours; "Other brands" is the one neutral. */
const BRAND_COLOURS = ['var(--series-1)', 'var(--series-2)', 'var(--series-3)', 'var(--series-4)', 'var(--owner-4)'] as const;
const NEUTRAL = 'var(--ink-subtle)';

const colourAt = (index: number, s: BrandSeries): string =>
  s.kind === 'other' ? NEUTRAL : (BRAND_COLOURS[index] ?? NEUTRAL);

interface SalesDrill {
  filter: SalesLinesFilter;
  title: string;
}

interface StockDrill {
  filter: BikeStockFilter;
  title: string;
}

/** Insights, Bikes: units sold by month and brand, and the bikes still unsold by age. */
export function BikesTab({ year }: { year: number }): ReactNode {
  const { t, i18n } = useTranslation();
  const [salesDrill, setSalesDrill] = useState<SalesDrill | null>(null);
  const [stockDrill, setStockDrill] = useState<StockDrill | null>(null);

  const summary = useQuery({
    queryKey: ['analytics', 'bikes', 'summary', year],
    queryFn: () => fetchBikesSummary(year),
  });
  const language = i18n.language;

  const series = useMemo(() => foldBrands(summary.data?.sold ?? []), [summary.data]);
  const seriesName = (s: BrandSeries): string =>
    s.kind === 'other' ? t('insights.bikes.otherBrands') : (s.brand ?? t('insights.bikes.noBrand'));

  const option = useMemo((): EChartOption => {
    if (summary.data === undefined) return {};
    const axisText = { color: 'var(--ink-muted)' };
    const months = Array.from({ length: 12 }, (_, i) => i + 1);
    const byId = new Map(series.map((s) => [s.id, s]));
    return {
      grid: { left: 8, right: 8, top: 16, bottom: 8, containLabel: true },
      tooltip: {
        trigger: 'axis',
        axisPointer: { type: 'shadow' },
        formatter: unitsTooltipFormatter(
          (id, index) => {
            const units = id === undefined ? undefined : byId.get(id)?.units.get(index + 1);
            return units === undefined ? undefined : formatUnits(units);
          },
          (index) => `${monthName(index + 1, language, 'long')} ${String(year)}`,
        ),
        backgroundColor: 'var(--surface-raised)',
        borderColor: 'var(--line)',
        textStyle: { color: 'var(--ink)' },
        confine: true,
      },
      xAxis: {
        type: 'category',
        data: months.map((m) => monthName(m, language)),
        axisLabel: axisText,
        axisLine: { lineStyle: { color: 'var(--line-strong)' } },
        axisTick: { show: false },
      },
      yAxis: {
        type: 'value',
        minInterval: 1,
        axisLabel: axisText,
        splitLine: { lineStyle: { color: 'var(--line)' } },
      },
      series: series.map((s, index) => ({
        id: s.id,
        name: s.kind === 'other' ? t('insights.bikes.otherBrands') : (s.brand ?? t('insights.bikes.noBrand')),
        type: 'bar',
        stack: 'units',
        barMaxWidth: 32,
        cursor: 'pointer',
        itemStyle: { color: colourAt(index, s), borderColor: 'var(--surface)', borderWidth: 1 },
        emphasis: { focus: 'series' },
        data: months.map((m) => plotValue(s.units.get(m) ?? '0')),
      })),
    };
  }, [summary.data, series, language, year, t]);

  if (summary.error !== null) {
    const denied = summary.error instanceof ApiError && summary.error.status === 403;
    return <Alert type="error" showIcon message={t(denied ? 'insights.noAccess' : 'insights.loadFailed')} />;
  }
  if (summary.data === undefined) return <Skeleton active paragraph={{ rows: 8 }} />;

  const data: BikesSummary = summary.data;
  if (data.salesDataAsOf === null && data.stockDataAsOf === null) {
    return <Alert type="info" showIcon message={t('insights.noSales')} />;
  }

  const through = data.comparedThroughMonth;
  const lastYear = String(year - 1);
  const range =
    through === 1
      ? t('insights.sales.vsMonth', { from: monthName(1, language), year: lastYear })
      : t('insights.sales.vsRange', {
          from: monthName(1, language),
          to: monthName(through, language),
          year: lastYear,
        });

  const openMonth = (click: EChartClick): void => {
    const s = series.find((x) => x.id === click.seriesId);
    const month = click.dataIndex + 1;
    if (s === undefined) return;
    const monthLabel = monthName(month, language, 'long');
    const filter = soldBikesFilter(year, month, s);
    setSalesDrill({
      filter,
      title: t('insights.bikes.soldTitle', { brand: seriesName(s), month: monthLabel, year: String(year) }),
    });
  };

  const openBucket = (b: BikeStockBucket): void => {
    setStockDrill({
      filter: { bucket: b.bucket },
      title: t('insights.bikes.stockTitle', { bucket: t(`insights.bikes.bucket.${b.bucket}`) }),
    });
  };

  const columns: TableProps<BikeStockBucket>['columns'] = [
    {
      title: t('insights.bikes.age'),
      key: 'bucket',
      render: (_: unknown, b) => t(`insights.bikes.bucket.${b.bucket}`),
    },
    {
      title: t('insights.bikes.bikes'),
      key: 'bikes',
      align: 'right',
      render: (_: unknown, b) => (
        <Button
          type="link"
          className="tabular"
          style={{ padding: 0 }}
          onClick={() => {
            openBucket(b);
          }}
        >
          {b.bikes}
        </Button>
      ),
    },
    {
      title: t('insights.bikes.cost'),
      key: 'cost',
      align: 'right',
      render: (_: unknown, b) => formatMoney(b.cost),
    },
  ];

  return (
    <Flex vertical gap={16}>
      <Flex vertical>
        {data.salesDataAsOf !== null && (
          <Text type="secondary">
            {t('insights.dataAsOf', { at: formatDateTime(data.salesDataAsOf) })}
          </Text>
        )}
        {data.stockDataAsOf !== null && (
          <Text type="secondary">
            {t('insights.asRead', { at: formatDateTime(data.stockDataAsOf) })}
          </Text>
        )}
      </Flex>

      <Row gutter={[12, 12]}>
        <Col xs={24} sm={12} xl={8}>
          <Tile id="sold" label={t('insights.bikes.soldThisYear')} value={formatUnits(data.unitsThisYear)}>
            <Text type="secondary">
              {t('insights.bikes.vsLastYear', { range, units: formatUnits(data.unitsLastYearSamePeriod) })}
            </Text>
          </Tile>
        </Col>
        <Col xs={24} sm={12} xl={8}>
          <Tile id="unsold" label={t('insights.bikes.unsold')} value={String(data.stock.bikes)}>
            <Text type="secondary">{t('insights.bikes.unsoldHelp')}</Text>
          </Tile>
        </Col>
        <Col xs={24} sm={12} xl={8}>
          <Tile id="unsold-cost" label={t('insights.bikes.unsoldCost')} value={formatMoney(data.stock.cost)}>
            <Text type="secondary">{t('insights.bikes.unsoldCostHelp')}</Text>
          </Tile>
        </Col>
      </Row>

      <Card size="small" title={t('insights.bikes.chartTitle')}>
        <Flex vertical gap={8}>
          <Text type="secondary">{t('insights.bikes.chartHelp')}</Text>
          {series.length === 0 ? (
            <Text type="secondary">{t('insights.bikes.noSold')}</Text>
          ) : (
            <>
              <Flex gap={12} wrap component="ul" style={{ listStyle: 'none', margin: 0, padding: 0 }}>
                {series.map((s, index) => (
                  <Flex key={s.id} component="li" gap={6} align="center">
                    <span
                      aria-hidden
                      style={{ width: 12, height: 12, borderRadius: 3, background: colourAt(index, s) }}
                    />
                    <Text>{seriesName(s)}</Text>
                  </Flex>
                ))}
              </Flex>
              <EChart option={option} height={340} onClick={openMonth} />
            </>
          )}
        </Flex>
      </Card>

      <Card size="small" title={t('insights.bikes.ageTitle')} data-testid="stock-ages">
        <Flex vertical gap={8}>
          <Text type="secondary">{t('insights.bikes.ageHelp')}</Text>
          {data.stockDataAsOf === null ? (
            <Text type="secondary">{t('insights.nothingYet')}</Text>
          ) : (
            <Table<BikeStockBucket>
              rowKey="bucket"
              size="small"
              pagination={false}
              scroll={{ x: 'max-content' }}
              dataSource={data.stock.buckets}
              columns={columns}
            />
          )}
        </Flex>
      </Card>

      <SalesLinesDrawer
        filter={salesDrill?.filter ?? null}
        title={salesDrill?.title ?? ''}
        onClose={() => {
          setSalesDrill(null);
        }}
      />
      <BikeStockDrawer
        filter={stockDrill?.filter ?? null}
        title={stockDrill?.title ?? ''}
        onClose={() => {
          setStockDrill(null);
        }}
      />
    </Flex>
  );
}
